/**
 * 주문신청 가능여부 mock — 우리가 보유한 region 데이터 기반으로 시뮬레이션.
 *
 * 실제 백엔드는 주소 → 행정구역 매핑 → service_area 테이블 조회로 판단하지만,
 * mock은 단순화: 주소 문자열에 mock region 시도 이름이 포함되어 있으면 가능, 없으면 불가능.
 *
 * 정확한 시군구·동 매칭이 아니라 "어느 시도(서울/경기/인천)에 속하는지" 수준만 검증 — 디자인 검증용으로 충분.
 */

import { ApiError } from '@/types/api'
import type { OrderAvailability } from '@/types/order'
import { ORDER_ERROR_CODES } from '@/types/order'

/**
 * 우리 mock region 리스트(서울/경기/인천/경상)에 대응하는 시도 키워드.
 * 주소 문자열에 이 중 하나라도 포함되면 "가능"으로 판정.
 *
 * 경상은 hidden 처리됐지만 백엔드 응답에 있으면 가능 판정 — 클라 hidden은 UI 노출 정책일 뿐.
 *
 * MOCK_REGION_LIST에서 자동 추출하는 방식도 가능하나, region 이름과 시도 키워드가
 * 1:N 관계(경상 → 부산/경남)라 단순 자동화 어려움. region 추가 시 이 배열만 수동 갱신.
 */
const AVAILABLE_KEYWORDS: readonly string[] = [
  '서울', // 서울특별시
  '경기', // 경기도
  '인천', // 인천광역시
  '부산', // 부산광역시 (경상 광역권역 안)
  '경남', // 경상남도
  '경상', // 경상남도 별칭
]

/**
 * 주소 문자열로 가능/불가능 시뮬레이션.
 *
 * 가능: 200 OK 응답 ({ isAvailable: true })
 * 불가능: 422 E422200 ApiError throw — 실제 백엔드와 동일하게 에러로 분기되도록
 *
 * `MOCK_REGION_LIST`는 backwards reference로 import — region 추가 시 자동 반영 안 되니
 * 수동 갱신 필요. 단, 현재 region 4개라 변동 거의 없음.
 */
export const computeMockAvailability = (addressJibun: string): OrderAvailability => {
  const isAvailable = AVAILABLE_KEYWORDS.some((kw) => addressJibun.includes(kw))
  if (isAvailable) {
    return { isAvailable: true }
  }
  throw new ApiError(
    ORDER_ERROR_CODES.NOT_AVAILABLE_AREA,
    null,
    '서비스 가능 지역이 아닙니다.',
    422,
    `mock-${Date.now()}`
  )
}
